import type { RouteObject } from 'react-router-dom';

type LazyRouteResult = Required<Pick<RouteObject, 'Component'>> &
  Pick<RouteObject, 'ErrorBoundary'>;

/**
 * Creates a lazy() function for a route that loads a named export from a dynamic import.
 *
 * @param factory - Dynamic import function (e.g., `() => import('@pages/Login')`)
 * @param exportName - Named export to use as the route Component
 * @param errorBoundaryExport - Optional named export for a per-route ErrorBoundary
 *
 * @example
 * { path: 'login', lazy: lazyRoute(() => import('@pages/Login'), 'LoginPage') }
 */
export function lazyRoute<T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  exportName: keyof T & string,
  errorBoundaryExport?: keyof T & string,
): () => Promise<LazyRouteResult> {
  return async () => {
    const mod = await factory();

    const result: LazyRouteResult = {
      Component: mod[exportName] as LazyRouteResult['Component'],
    };

    if (errorBoundaryExport) {
      result.ErrorBoundary = mod[
        errorBoundaryExport
      ] as LazyRouteResult['ErrorBoundary'];
    }

    return result;
  };
}
